import { X, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

export function PainPoints() {
  const { t } = useTranslation();

  const before = t("painpoints.before", { returnObjects: true }) as string[];
  const after = t("painpoints.after", { returnObjects: true }) as string[];

  return (
    <section className="bg-white py-[96px] overflow-hidden">
      <div className="mx-auto max-w-[1200px] px-6">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center text-[36px] sm:text-[48px] leading-[1.15] font-bold tracking-[-0.02em] text-foreground max-w-[720px] mx-auto"
        >
          {t("painpoints.title")}
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mx-auto mt-4 max-w-[640px] text-center text-[17px] leading-relaxed text-foreground/50 sm:text-[18px]"
        >
          {t("painpoints.subtitle")}
        </motion.p>

        <div className="mt-16 grid lg:grid-cols-2 gap-10 lg:gap-16 relative">
          <div className="hidden lg:block absolute top-0 bottom-0 left-1/2 w-px bg-gradient-to-b from-transparent via-foreground/10 to-transparent" />

          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold tracking-[0.08em] uppercase bg-red-100 text-red-600">
              {t("painpoints.before_label")}
            </span>
            <ul className="mt-6 space-y-4">
              {before.map((item, idx) => (
                <motion.li
                  key={item}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.1 }}
                  className="flex gap-3"
                >
                  <span className="mt-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-500 shrink-0">
                    <X className="w-3.5 h-3.5" strokeWidth={3} aria-hidden="true" />
                  </span>
                  <span className="text-[16px] text-foreground/80 leading-[1.6]">{item}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold tracking-[0.08em] uppercase bg-primary/10 text-primary">
              {t("painpoints.after_label")}
            </span>
            <ul className="mt-6 space-y-4">
              {after.map((item, idx) => (
                <motion.li
                  key={item}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.1 }}
                  className="flex gap-3"
                >
                  <span className="mt-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary shrink-0">
                    <Check className="w-3.5 h-3.5" strokeWidth={3} aria-hidden="true" />
                  </span>
                  <span className="text-[16px] text-foreground leading-[1.6] font-medium">{item}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
